import { useState } from "react";
import { Link } from "react-router";
import { describeError } from "@/api/client";
import { useCanEdit } from "@/api/session";
import {
  AlertDialog,
  Badge,
  Button,
  DataTable,
  EmptyState,
  InlineAlert,
  Meter,
  Money,
  Select,
  Skeleton,
  Stat,
  StatGroup,
  Toolbar,
  useToast,
  type DataTableColumn,
  type SelectOption,
} from "@/components";
import { formatFractionAsPercent, formatInteger } from "@/lib/format";
import { useSearchParamsInPlace } from "@/lib/hooks";
import { AddToBasketDialog } from "./AddToBasketDialog";
import { useCoverage, useDeleteReferenceList, useReferenceLists, type CoverageItem, type CoverageStatus } from "./api";
import styles from "./KnowledgePage.module.css";
import { NewListDialog } from "./NewListDialog";

const STATUS_LOOK: Record<CoverageStatus, { label: string; tone: "success" | "info" | "warning" | "neutral" }> = {
  owned: { label: "Owned", tone: "success" },
  owned_variant: { label: "Owned as a variant", tone: "info" },
  missing: { label: "Missing", tone: "warning" },
  unresolved: { label: "Unknown name", tone: "neutral" },
};

const REASONS: Record<string, string> = {
  same_substance: "same substance",
  same_parent_substance: "same parent substance",
  same_structure: "same structure",
  same_species: "same plant species",
};

const STATUS_FILTERS: ReadonlyArray<SelectOption> = [
  { value: "all", label: "All items" },
  { value: "missing", label: "Missing" },
  { value: "owned", label: "Owned" },
  { value: "owned_variant", label: "Owned as a variant" },
  { value: "unresolved", label: "Unknown names" },
];

function isStatus(value: string | null): value is CoverageStatus {
  return value === "owned" || value === "owned_variant" || value === "missing" || value === "unresolved";
}

/** How much of a reference list (JCE, the Reddit beginner list, your own) the workspace owns. */
export function CoverageTab() {
  const lists = useReferenceLists();
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const canEdit = useCanEdit();
  const toast = useToast();
  const remove = useDeleteReferenceList();
  const [newOpen, setNewOpen] = useState(false);
  const [basketOpen, setBasketOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const listParam = searchParams.get("list");
  const statusParam = searchParams.get("status");
  const status = isStatus(statusParam) ? statusParam : null;
  const selectedId = listParam ?? lists.data?.[0]?.id ?? null;
  const selectedSummary = lists.data?.find((list) => list.id === selectedId) ?? null;
  const coverage = useCoverage(selectedId);

  function setParam(name: string, value: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (!value || value === "all") next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: true },
    );
  }

  if (lists.isPending)
    return (
      <div className={styles.panel}>
        <Skeleton lines={6} />
      </div>
    );
  if (lists.isError) {
    return (
      <div className={styles.panel}>
        <InlineAlert
          tone="danger"
          title="Couldn’t load the reference lists"
          action={<Button onClick={() => void lists.refetch()}>Retry</Button>}
        >
          {lists.error.message}
        </InlineAlert>
      </div>
    );
  }
  if (!lists.data.length) {
    return (
      <div className={styles.panel}>
        <EmptyState
          title="No reference lists yet"
          description="A reference list is a set of names to compare your organ against: a beginner kit, a course list, a book’s palette."
          actions={
            canEdit ? (
              <Button variant="primary" onClick={() => setNewOpen(true)}>
                New list from names…
              </Button>
            ) : undefined
          }
        />
        <NewListDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => setParam("list", id)} />
      </div>
    );
  }

  const data = coverage.data;
  const totals = data?.totals ?? selectedSummary?.totals ?? null;
  const resolved = totals ? totals.total - totals.unresolved : 0;
  const covered = totals ? totals.owned + totals.owned_variant : 0;
  const rows = data ? (status ? data.items.filter((item) => item.status === status) : data.items) : undefined;
  const missingWithMaterial = (data?.items ?? []).filter((item) => item.status === "missing" && item.material);

  const columns: DataTableColumn<CoverageItem>[] = [
    { id: "position", header: "#", numeric: true, width: "fit", cell: (item) => item.position },
    {
      id: "name",
      header: "Name on the list",
      width: "wide",
      cell: (item) => (
        <span className={styles.statusCell}>
          <span>{item.raw_name}</span>
          {item.material && item.material.name !== item.raw_name ? (
            <Link to={`/materials/${item.material.id}`} className={styles.subtle}>
              {item.material.name}
            </Link>
          ) : item.material ? (
            <Link to={`/materials/${item.material.id}`} className={styles.subtle}>
              Open material
            </Link>
          ) : null}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (item) => {
        const look = STATUS_LOOK[item.status];
        return (
          <span className={styles.statusCell}>
            <Badge
              tone={look.tone}
              icon={item.status === "owned" ? "check" : item.status === "unresolved" ? "helpCircle" : undefined}
              dashed={item.status === "unresolved"}
            >
              {look.label}
            </Badge>
            {item.status === "owned_variant" && item.matched.length ? (
              <span className={styles.subtle}>
                {item.matched.map((match) => match.name).join(", ")}
                {item.reason ? `, ${REASONS[item.reason] ?? item.reason}` : ""}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "offer",
      header: "Best offer per neat g",
      numeric: true,
      width: "fit",
      cell: (item) =>
        item.best_price ? (
          <span className={styles.priceCell}>
            <Money
              minor={item.best_price.price_per_neat_g_minor}
              currency={item.best_price.currency}
              extraDecimals={2}
              per="g"
            />
            <span className={styles.subtle}>{item.best_price.supplier}</span>
          </span>
        ) : (
          <span className={styles.subtle}>{item.status === "missing" ? "No offer" : ""}</span>
        ),
    },
  ];

  return (
    <div className={styles.panel}>
      <div className={styles.coverageHeader}>
        <Toolbar label="Choose a reference list">
          <Select
            label="Reference list"
            options={lists.data.map((list) => ({
              value: list.id,
              label: list.name,
              detail: `${formatInteger(list.item_count)} items${list.is_private ? ", yours" : ""}`,
            }))}
            value={selectedId}
            onValueChange={(value) => setParam("list", value)}
          />
          <Select
            label="Show"
            options={STATUS_FILTERS}
            value={status ?? "all"}
            onValueChange={(value) => setParam("status", value)}
          />
        </Toolbar>
        {canEdit ? (
          <div className={styles.actions}>
            <Button onClick={() => setNewOpen(true)}>New list from names…</Button>
            {selectedSummary?.is_private ? (
              <Button variant="ghost" icon="trash" onClick={() => setDeleteOpen(true)}>
                Delete list…
              </Button>
            ) : null}
            <Button
              variant="primary"
              icon="cart"
              disabled={!missingWithMaterial.length}
              focusableWhenDisabled
              onClick={() => setBasketOpen(true)}
            >
              Add missing to a basket…
            </Button>
          </div>
        ) : null}
      </div>

      {totals ? (
        <div className={styles.coverageSummary}>
          <Meter
            label="Covered"
            value={covered}
            max={Math.max(resolved, 1)}
            level={covered === 0 ? "empty" : "ok"}
            valueText={`${formatInteger(covered)} of ${formatInteger(resolved)} known items (${formatFractionAsPercent(totals.covered_fraction, { decimals: 0 })})`}
          />
          <StatGroup>
            <Stat label="Owned" value={formatInteger(totals.owned)} />
            <Stat
              label="As a variant"
              value={formatInteger(totals.owned_variant)}
              hint="Same substance, parent or plant"
            />
            <Stat label="Missing" value={formatInteger(totals.missing)} />
            <Stat label="Unknown names" value={formatInteger(totals.unresolved)} hint="Left out of the share" />
          </StatGroup>
        </div>
      ) : null}

      <DataTable
        caption={`Items of ${selectedSummary?.name ?? "the list"}`}
        columns={columns}
        rows={rows}
        getRowId={(item) => item.id}
        loading={coverage.isPending}
        error={coverage.error}
        onRetry={() => void coverage.refetch()}
        stickyHeader
        empty={
          <EmptyState
            variant="inline"
            headingLevel={3}
            title={status ? `No ${STATUS_LOOK[status].label.toLowerCase()} items` : "This list is empty"}
            actions={status ? <Button onClick={() => setParam("status", null)}>Show all items</Button> : undefined}
          />
        }
      />
      <p className={styles.footnote}>
        A variant is another material you own that can stand in: the same substance under another trade name, a stereo
        variant or grade of the same parent, or another oil of the same plant.
      </p>

      <NewListDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => setParam("list", id)} />
      {data ? (
        <AddToBasketDialog
          open={basketOpen}
          onOpenChange={setBasketOpen}
          listName={data.reference_list.name}
          items={missingWithMaterial}
          unresolvedCount={totals?.unresolved ?? 0}
        />
      ) : null}
      {selectedSummary?.is_private ? (
        <AlertDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title={`Delete ${selectedSummary.name}?`}
          description="The list and its items are removed. Materials and stock are not affected."
          confirmLabel={`Delete ${selectedSummary.name}`}
          pending={remove.isPending}
          onConfirm={() =>
            remove.mutate(selectedSummary.id, {
              onSuccess: () => {
                toast.success(`${selectedSummary.name} deleted`);
                setParam("list", null);
                setDeleteOpen(false);
              },
              onError: (error) => toast.error("Couldn’t delete the list", { description: describeError(error) }),
            })
          }
        />
      ) : null}
    </div>
  );
}
